import IconConnectors from '@apollo/icons/default/IconConnectors.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconSingleService from '@apollo/icons/default/IconSingleService.svg';
import {
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
} from '@apollo/orbit';
import { GraphQLNamedType } from 'graphql';
import React from 'react';

import { FieldType } from 'src/app/graph/insights/fieldPage/useCurrentSchemaCoordinate';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import {
  GetSubgraphs,
  SubgraphAndMetadata,
} from 'src/lib/graphql/subgraphMetadataHelpers';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { sdlRouteConfig } from '../../../routes';

import { FieldSummaryEmptyText } from './FieldSummaryEmptyText';

const maxVisibleSubgraphs = 5;

const SubgraphLink = ({
  graphId,
  graphVariant,
  subgraph,
}: {
  graphId: string;
  graphVariant: string;
  subgraph: SubgraphAndMetadata;
}) => (
  <Link
    className="flex items-center"
    to={sdlRouteConfig.location({
      graphId,
      graphVariant,
      selectedSchema: subgraph.name,
    })}
    data-analytics-category="Field Insights"
    data-analytics-label="Subgraph link click"
  >
    {subgraph.isConnector ? (
      <IconConnectors className="top-1.5 mr-1.5 text-icon-primary" />
    ) : (
      <IconSingleService className="top-1.5 mr-1.5 text-icon-primary" />
    )}
    {subgraph.name}
  </Link>
);

interface Props {
  field: FieldType;
  parentType: GraphQLNamedType;
  graphId: string;
  variant: string;
  getSubgraphs: GetSubgraphs | null | undefined;
}

export const FieldPageSubgraphs = ({
  field,
  parentType,
  variant: graphVariant,
  graphId,
  getSubgraphs,
}: Props) => {
  // @ts-expect-error TODO: get this working for enum values
  const subgraphs = getSubgraphs?.({ node: field, parentType });
  if (!isMinLengthArray(1, subgraphs)) {
    return (
      <Tooltip
        className="w-[228px]"
        interactive
        placement="bottom-start"
        label={
          <div>
            Learn how to define subgraphs{' '}
            <Link
              isExternal
              to="https://www.apollographql.com/docs/apollo-server/using-federation/apollo-subgraph-setup/"
            >
              here
            </Link>
            .
          </div>
        }
      >
        <span className="flex items-center whitespace-nowrap">
          <FieldSummaryEmptyText message="No subgraphs found." />
          <IconInfo className="ml-2 pr-1 pt-1 text-secondary" />
        </span>
      </Tooltip>
    );
  }
  const visibleSubgraphs = subgraphs.slice(0, maxVisibleSubgraphs);
  const hiddenSubgraphCount = subgraphs.length - visibleSubgraphs.length;

  return (
    <ul className="flex flex-1 flex-wrap">
      {visibleSubgraphs.map((subgraph) => (
        <li className="mr-2" key={subgraph.name}>
          <SubgraphLink
            graphId={graphId}
            graphVariant={graphVariant}
            subgraph={subgraph}
          />
        </li>
      ))}
      {hiddenSubgraphCount > 0 && (
        <Popover trigger="click">
          <PopoverTrigger>
            <span className="whitespace-nowrap text-secondary">
              (
              <ClickableText className="text-link">
                + {hiddenSubgraphCount} more
              </ClickableText>
              )
            </span>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              className="max-h-[calc(100vh-500px)] min-h-[180px] w-auto min-w-[240px] max-w-[480px] overflow-hidden overflow-y-auto border border-solid border-primary bg-primary focus:shadow-none"
              data-testid="all-subgraphs"
            >
              <PopoverBody className="relative p-0">
                <h4 className="sticky top-0 flex items-center border-b border-primary bg-white px-4 py-2 font-semibold text-black">
                  Subgraphs{' '}
                  <span className="ml-1 rounded bg-info px-1 text-xs text-info">
                    {subgraphs.length}
                  </span>
                </h4>
                <ul className="px-4 py-2">
                  {subgraphs.map((subgraph) => (
                    <li
                      className="mr-2 inline whitespace-nowrap"
                      key={subgraph.name}
                    >
                      <SubgraphLink
                        graphId={graphId}
                        graphVariant={graphVariant}
                        subgraph={subgraph}
                      />
                    </li>
                  ))}
                </ul>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      )}
    </ul>
  );
};
